import React, { useEffect, useState } from 'react';
import s from './Purchases.module.scss';
import ProfileMenuHeader from '../ProfileMenuHeader';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useGetPruchases from 'sdk/hooks/shop/useGetPruchases';
import { processRefund } from 'sdk/hooks/payments/useProcessRefund';
import PurchasesList from './PurchasesList';
import RefundReasons from './RefundReasons';
import Button from 'components/shared/Button';
import UploadImage from './UploadImage';
import Confirmation from './Confirmation';
import Success from './Success';
import { useNavigate } from 'react-router-dom';
import { useGetMyUser } from 'sdk/hooks/user/useGetMyUser';

enum PurchasesStep {
  PURCHASES = 'purchases',
  REFUND_REASONS = 'refund_reasons',
  UPLOAD_IMAGES = 'upload_images',
  CONFIRMATION = 'confirmation',
  SUCCESS = 'success',
}

const Purchases = () => {
  const navigate = useNavigate();
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { data: purchases, refetch } = useGetPruchases(userId);

  const { data: userData } = useGetMyUser(userId || '');
  const [refundLoading, setRefundLoading] = useState(false);
  const [step, setStep] = useState(PurchasesStep.PURCHASES);
  const [refundReasons, setRefundReasons] = useState<string>();
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [orderId, setOrderId] = useState<string>('');
  const [price, setPrice] = useState<number>();
  const [productName, setProductName] = useState<string>('');
  const [tryotoOrderId, setTryotoOrderId] = useState<string>('');
  const isPurchasesStep = PurchasesStep.PURCHASES;
  const isRefundReasonsStep = PurchasesStep.REFUND_REASONS;
  const isUploadImagesStep = PurchasesStep.UPLOAD_IMAGES;
  const isConfirmationStep = PurchasesStep.CONFIRMATION;
  const isSuccessStep = PurchasesStep.SUCCESS;

  const userName = userData?.username;
  const userPhone = userData?.phone;

  const handleClose = () => {
    if (step === PurchasesStep.PURCHASES) {
      navigate('/profile-menu');
      return;
    }

    if (step === PurchasesStep.REFUND_REASONS) {
      setRefundReasons('');
      setStep(PurchasesStep.PURCHASES);
      return;
    }

    if (step === PurchasesStep.UPLOAD_IMAGES) {
      setMediaUrls({});
      setStep(PurchasesStep.REFUND_REASONS);
      return;
    }

    if (step === PurchasesStep.CONFIRMATION) {
      setStep(PurchasesStep.UPLOAD_IMAGES);
      return;
    }
  };

  const handleCancellation = async (price, id, refundReasons, mediaUrls) => {
    setRefundLoading(true);
    // id - order_id in order_items table
    
    try {
      await processRefund({
        price,
        id,
        refundReasons,
        mediaUrls,
        type: 'Product',
        userName,
        userPhone,
        productName,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setRefundLoading(false);
      setStep(isSuccessStep);
    }
  };

  const refundInTryoto = async orderIds => {
    try {
      const req = await fetch(
        'https://gibnkggiuxphkienkxwf.supabase.co/functions/v1/refund-tryoto-order',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdpYm5rZ2dpdXhwaGtpZW5reHdmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzA3OTI0OTEsImV4cCI6MjA0NjM2ODQ5MX0.jzHj8vjuo7KuX4nHi-_tq051mp9quUNKMP0wN8Makj4`, // Замените YOUR_ACCESS_TOKEN на ваш токен
          },
          body: JSON.stringify({ id: orderIds }),
        },
      );

      const data = await req.json();

      return data;
    } catch (error) {
      console.error('Error fetching order history:', error);
    }
  };

  const handleContinue = () => {
    if (step === isRefundReasonsStep) {
      setStep(isUploadImagesStep);
    }
    if (step === isUploadImagesStep) {
      setStep(isConfirmationStep);
    }

    if (step === isConfirmationStep) {
      handleCancellation(price, orderId, refundReasons, mediaUrls);
      refundInTryoto(tryotoOrderId);
    }

    if (step === isSuccessStep) {
      navigate('/profile-menu');
    }
  };

  const disabledContinueButton = () => {
    if (step === isRefundReasonsStep) {
      return !refundReasons;
    }
    if (step === isUploadImagesStep) {
      return Object.keys(mediaUrls).length < 4;
    }
    if (step === isConfirmationStep) {
      return false;
    }
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div style={{ overflow: 'auto' }}>
      <div className={s.purchases_container}>
        {step !== isSuccessStep && <ProfileMenuHeader text="My purchases" onClose={handleClose} />}
        {step === isPurchasesStep && (
          <PurchasesList
            purchases={purchases}
            refundLoading={refundLoading}
            handleNext={() => setStep(isRefundReasonsStep)}
            setOrderId={setOrderId}
            setPrice={setPrice}
            setProductName={setProductName}
            setTryotoOrderId={setTryotoOrderId}
          />
        )}
        {step === isRefundReasonsStep && (
          <RefundReasons refundReasons={refundReasons} setRefundReasons={setRefundReasons} />
        )}
        {step === isUploadImagesStep && (
          <UploadImage mediaUrls={mediaUrls} setMediaUrls={setMediaUrls} />
        )}
        {step === isConfirmationStep && <Confirmation />}
        {step === isSuccessStep && <Success />}
        {step === isPurchasesStep ? (
          <></>
        ) : (
          <Button
            wide
            darkGreen
            className={s.continue_button}
            onClick={handleContinue}
            disabled={disabledContinueButton()}
            loading={refundLoading}
          >
            {step === isConfirmationStep
              ? 'Confirm'
              : step === isSuccessStep
              ? 'Back to shopping'
              : 'Continue'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Purchases;
